import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  updateUserProfile,
} from "@/services/api-client";
import {
  DECLARED_CERTIFICATE_OTHER_CODE,
  INDUSTRY_SECTOR_OTHER_CODE,
} from "@/constants/company-form-options";
import { isValidPolishNip, normalizeNip } from "@/utils/nip";
import { useAuth } from "@/contexts/useAuth";
import { useUnsavedChanges } from "@/services/app-update/unsaved-changes";
import {
  type CompanyProfileFormData,
  apiToFormData,
  emptyProfile,
} from "./profile";

interface UseSettingsProfileResult {
  profileData: CompanyProfileFormData;
  profileStatus: string | null;
  profileError: string | null;
  profileLoading: boolean;
  profileSaving: boolean;
  handleProfileChange: (
    field: keyof CompanyProfileFormData,
    value: string | string[],
  ) => void;
  handleIndustryToggle: (value: string) => void;
  handleCertificateToggle: (value: string) => void;
  handleProfileSave: (event: React.FormEvent) => Promise<void>;
}

export function useSettingsProfile(): UseSettingsProfileResult {
  const { t } = useTranslation(["settings", "validation"]);
  const { profile: authProfile, refreshProfile } = useAuth();
  const [profileData, setProfileData] = useState<CompanyProfileFormData>(emptyProfile);
  const [profileStatus, setProfileStatus] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  // Derive profile data from useAuth()'s profile (which already contains
  // companyData from /auth/me). Avoids a duplicate GET on mount (audit
  // 2026-08-25 D.8). Memoised so the form doesn't re-init on every parent
  // re-render — apiToFormData allocates fresh objects each call.
  const initialProfileData = useMemo(
    () => apiToFormData(authProfile?.companyData ?? null),
    [authProfile?.companyData],
  );

  useEffect(() => {
    setProfileData(initialProfileData);
  }, [initialProfileData]);
  useUnsavedChanges(
    JSON.stringify(profileData) !== JSON.stringify(initialProfileData),
  );
  const handleProfileChange = (
    field: keyof CompanyProfileFormData,
    value: string | string[],
  ) => setProfileData((prev) => ({ ...prev, [field]: value }));

  const handleIndustryToggle = (value: string) => {
    const selected = profileData.industry_sector;
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    setProfileData((prev) => ({
      ...prev,
      industry_sector: next,
      industry_sector_other: next.includes(INDUSTRY_SECTOR_OTHER_CODE)
        ? prev.industry_sector_other
        : "",
    }));
  };

  const handleCertificateToggle = (value: string) => {
    const selected = profileData.declared_certificate;
    const next = selected.includes(value)
      ? selected.filter((item) => item !== value)
      : [...selected, value];
    setProfileData((prev) => ({
      ...prev,
      declared_certificate: next,
      declared_certificate_other: next.includes(DECLARED_CERTIFICATE_OTHER_CODE)
        ? prev.declared_certificate_other
        : "",
    }));
  };

  const handleProfileSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setProfileSaving(true);
    setProfileStatus(null);
    setProfileError(null);
    try {
      const normalizedNip = normalizeNip(profileData.nip);
      if (!normalizedNip) return setProfileError(t("validation:company.nipRequired"));
      if (!isValidPolishNip(normalizedNip)) {
        return setProfileError(t("validation:company.nipInvalid"));
      }
      if (profileData.industry_sector.length === 0) {
        return setProfileError(t("validation:company.industrySectorRequired"));
      }
      if (profileData.industry_sector.includes(INDUSTRY_SECTOR_OTHER_CODE) &&
        !profileData.industry_sector_other.trim()) {
        return setProfileError(t("validation:company.industrySectorOtherRequired"));
      }

      await updateUserProfile({
        ...profileData,
        industry_sector: profileData.industry_sector,
        industry_sector_other: profileData.industry_sector.includes(INDUSTRY_SECTOR_OTHER_CODE)
          ? profileData.industry_sector_other.trim()
          : undefined,
        declared_certificate: profileData.declared_certificate,
        declared_certificate_other:
          profileData.declared_certificate.includes(DECLARED_CERTIFICATE_OTHER_CODE)
            ? profileData.declared_certificate_other.trim()
            : undefined,
        nip: normalizedNip,
      });
      // Re-fetch the profile via useAuth(). refreshProfile triggers the same
      // /auth/me fetch AuthContext already uses; the new profile (with updated
      // companyData) flows back through context into `initialProfileData` and
      // re-seeds `profileData`. This replaces the previous direct local update.
      await refreshProfile();
      setProfileStatus(t("settings:success.profileUpdated"));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setProfileError(t("settings:errors.signInRequired"));
      } else {
        setProfileError(t("settings:errors.saveProfileFailed"));
      }
    } finally {
      setProfileSaving(false);
    }
  };

  return {
    profileData,
    profileStatus,
    profileError,
    profileLoading,
    profileSaving,
    handleProfileChange,
    handleIndustryToggle,
    handleCertificateToggle,
    handleProfileSave,
  };
}
